import type { ArticleBundle } from './types';

/**
 * IndexedDB-backed persistent cache for article asset bundles.
 *
 * Stores one entry per article (all chunks + inflections bundled).
 * LRU eviction when entry count exceeds MAX_ENTRIES.
 */

const DB_NAME = 'polyreader-assets';
// v4: audio bytes moved out of the bundle. Each chunk now carries an
// `audioUrl` pointer instead of base64 audio + audioFormat. Bumping the
// DB version forces the onupgradeneeded handler to drop the old store
// and recreate it, so existing cached bundles (which still have the old
// audio fields and no audioUrl) get wiped and re-fetched on next load.
const DB_VERSION = 4;
const STORE_NAME = 'articles';
const MAX_ENTRIES = 50;
const EVICTION_TARGET = 40;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      // Delete old stores from previous versions
      if (db.objectStoreNames.contains('chunks')) db.deleteObjectStore('chunks');
      if (db.objectStoreNames.contains(STORE_NAME)) db.deleteObjectStore(STORE_NAME);
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'articleId' });
      store.createIndex('by-accessed', 'accessedAt', { unique: false });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

export async function getCachedArticleAssets(articleId: string): Promise<ArticleBundle | null> {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(articleId);
    request.onsuccess = () => {
      const record = request.result;
      if (!record) {
        resolve(null);
        return;
      }
      record.accessedAt = new Date().toISOString();
      store.put(record);
      resolve(record.bundle);
    };
    request.onerror = () => resolve(null);
  });
}

export async function setCachedArticleAssets(articleId: string, bundle: ArticleBundle): Promise<void> {
  const db = await openDB();
  const now = new Date().toISOString();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put({
      articleId,
      bundle,
      cachedAt: now,
      accessedAt: now,
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  evictIfNeeded().catch(() => {});
}

async function evictIfNeeded(): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);

  const countRequest = store.count();
  const count = await new Promise<number>((resolve) => {
    countRequest.onsuccess = () => resolve(countRequest.result);
    countRequest.onerror = () => resolve(0);
  });

  if (count <= MAX_ENTRIES) return;

  const toDelete = count - EVICTION_TARGET;
  const index = store.index('by-accessed');
  let deleted = 0;

  return new Promise((resolve) => {
    const cursor = index.openCursor();
    cursor.onsuccess = () => {
      const c = cursor.result;
      if (!c || deleted >= toDelete) {
        resolve();
        return;
      }
      c.delete();
      deleted++;
      c.continue();
    };
    cursor.onerror = () => resolve();
  });
}
